import type { BadgeTone } from '../ui';
import { Badge } from '../ui';
import type { Post, PostStatus } from '../../api/posts';
import './StoryStanding.css';

export interface StoryStandingProps {
  post: Post;
}

const TONES: Record<PostStatus, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'accent',
  REJECTED: 'warning',
  PUBLISHED: 'success',
  UNPUBLISHED: 'warning',
  HIDDEN: 'warning',
};

const LABELS: Record<PostStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Waiting for review',
  REJECTED: 'Not approved',
  PUBLISHED: 'Published',
  UNPUBLISHED: 'Taken down',
  HIDDEN: 'Taken down by a moderator',
};

/**
 * Where a story stands, in a sentence.
 *
 * The distinction this component exists to make visible is the one the whole
 * phase turns on: what a **reader** sees and what the **author** is writing are
 * two different versions, and both are named here so nobody has to guess which
 * one they are looking at.
 */
function standingSentence(post: Post): string {
  const live = post.published_version;
  const draft = post.draft_version;
  const waiting = post.review_version;

  if (post.status === 'PUBLISHED') {
    const read = `Version ${live} is the one people read.`;
    if (waiting !== null) return `${read} Version ${waiting} is waiting for review.`;
    return draft === null
      ? read
      : `${read} Version ${draft} is a draft, and stays private until you publish it.`;
  }

  if (post.status === 'PENDING_REVIEW') {
    return `Version ${waiting ?? post.version?.number ?? ''} is waiting for review. Nothing is public yet.`;
  }

  if (post.status === 'REJECTED') {
    // The reason is shown in full beside this, not summarised into it: it is
    // the only part of a rejection an author can act on.
    return 'A moderator did not approve this version. You can change it and send it back.';
  }

  if (post.status === 'HIDDEN') {
    return 'A moderator has taken this story down. You can still read it; nobody else can.';
  }

  if (post.status === 'UNPUBLISHED') {
    return draft === null
      ? 'This story has been taken down. Its earlier versions are kept.'
      : `This story has been taken down. Version ${draft} is a draft.`;
  }

  return draft === null
    ? 'Not published yet.'
    : `Draft version ${draft}. Nobody else can read this yet.`;
}

export function StoryStanding({ post }: StoryStandingProps) {
  return (
    <p className="sc-standing">
      <Badge tone={TONES[post.status]} srPrefix="Status">
        {LABELS[post.status]}
      </Badge>
      <span className="sc-standing__text">{standingSentence(post)}</span>
    </p>
  );
}
